import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useEffect, useState } from "react"
import api from "@/lib/axiosInstance"
import { Link } from "react-router-dom"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { AlertCircle } from "lucide-react"
import { Spinner } from "@/components/ui/shadcn-io/spinner"

export default function ForgotPassword() {
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [username, setUsername] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null);
        setSuccess(null);
        var overwrite = false;
        try {
            if(!username) {
                setValidationErrors(['username']);
                throw new Error('Please complete all required fields.');
            }
            setSubmitting(true);
            var formdata = new FormData();
            formdata.append('email', username);

            const response = await api.post(`/agents/password/forgot`, formdata);
            if(!response.data.status) {
                if(response.data.overwriteErrorAsSuccess) {
                    overwrite = true;
                }
                throw new Error(response.data.error);
            }
            setSuccess(response.data.message);
        } catch (err : any) {
            const error = err.response ? err.response.data.error : err.message;
            if(!overwrite) {
                setError(error);
            } else {
                setSuccess(error);
            }
        } finally {
            setSubmitting(false);
        }
    }

    useEffect(() => {
        if(error) {
            setSuccess(null);
        }
    }, [error]);

    useEffect(() => {
        if(success) {
            setError(null);
        }
    }, [success]);

    return (
        <div className="flex items-center justify-center h-screen w-screen">
            <Card className="w-[380px] shadow-xl border-0">
                <CardHeader>
                    <CardTitle className="text-center text-2xl font-bold">Forgot Password</CardTitle>
                    <CardDescription className="text-center">
                        Request for a change
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {
                        error && (
                            <Alert variant="destructive" className="mb-3 bg-red-100 border-red-300">
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )
                    }
                    {
                        success && (
                            <Alert className="mb-3 bg-green-100 border-green-300">
                                <AlertCircle className="h-4 w-4" color="green"/>
                                <AlertTitle>Success</AlertTitle>
                                <AlertDescription>{success}</AlertDescription>
                            </Alert>
                        )
                    }
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                            id="email"
                            type="email"
                            className={`${validationErrors.length ? 'border-red-400' : ''}`}
                            placeholder="you@example.com"
                            value={username}
                            onChange={(e) => {
                                setUsername(e.target.value);
                                if(e.target.value) {
                                    setValidationErrors([]);
                                }
                            }}/>
                            {
                                validationErrors.length > 0 && (
                                    <p className="text-xs text-red-400">Email is required</p>
                                )
                            }
                        </div>
                        <Button type="submit" className="w-full" disabled={submitting}>
                            {
                                submitting && (
                                    <Spinner />
                                )
                            }
                            Reset your password
                        </Button>
                        <div className="flex mt-1">
                            <Link to={'/login'} className="text-sm text-black! hover:underline!">Back</Link>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}
